/**
 * CARE HOME — its nine screens, as a strip group.
 *
 * WHY IT IS HERE AND NOT IN CATEGORIES. Care Home left the hub on 2026-09-25
 * and became its own sidebar section, because the hub's link is labelled
 * "Dispensary Management" and care-home work is not dispensary management.
 * `categories.shell29.test.ts` holds that decision in place: it asserts that
 * every one of these paths returns null from `categoryForModulePath`. So this
 * is a group the STRIPS know about and the hub does not, which is exactly the
 * shape the section wanted.
 *
 * WHY IT IS A `Category`. Reagan, 2026-09-29: "all care home stuff on side bar
 * needs putting in the care home similar to what we have done is dispensary
 * management". The strips already render a category — banks across the top,
 * that bank's modules down the left — so borrowing the shape means the two
 * sections cannot drift apart in how they look or behave. Care Home has no
 * sibling banks, so the strip renders with no top bar; see
 * PharmacyModuleStrips.
 *
 * THE SIDEBAR AND THIS LIST ARE THE SAME LIST. It used to be
 * `CARE_HOME_MODULES` in Sidebar.tsx, nine rows deep under the section. Those
 * nine names and these nine names on one screen, three inches apart, is the
 * same duplication that took the banks out of the sidebar the day before —
 * so the sidebar now reads this and shows one row.
 *
 * THE ICONS ARE NEW. All nine carried `Icon.auditPack`, so the sidebar drew
 * the same picture nine times, which is the same as drawing none.
 */
import type { Category } from './categories';

export const CARE_HOME: Category = {
  id: 'care-home',
  label: 'Care Home',
  blurb: 'Paperwork, cycles, dossettes, contracts & audits',
  icon: 'home',
  countUnit: 'screens',
  tiles: [
    /* Receive Paperwork leads, for the same reason it led the sidebar list:
       the reorder sheet and the scripts arrive before anybody opens a cycle. */
    { to: '/care-home-receive', labelKey: 'care_home_receive', icon: 'download' },
    { to: '/care-home-cycles', labelKey: 'care_home_cycles', icon: 'handover' },
    { to: '/care-homes', labelKey: 'care_homes_mds', icon: 'home' },
    { to: '/care-home-contracts', labelKey: 'care_home_contracts', icon: 'file' },
    { to: '/blister-specials', labelKey: 'blister_specials', icon: 'box' },
    { to: '/dossette-admin', labelKey: 'dossette_admin', icon: 'pill' },
    { to: '/delivery-wall-chart', labelKey: 'delivery_wall_chart', icon: 'truck' },
    { to: '/daa', labelKey: 'daa_mca', icon: 'clipboard' },
    // PH-SHELL-29 (Kris, 2026-09-18): audits of the homes the pharmacy supplies.
    { to: '/care-home-audits', labelKey: 'care_home_audits', icon: 'checklist' },
  ],
};

/** Every route that renders inside the Care Home strip. */
export const CARE_HOME_PATHS: readonly string[] = (CARE_HOME.tiles ?? []).map((t) => t.to);

/**
 * The group a care-home route belongs to, or null.
 *
 * Same trailing-slash rule as `categoryForModulePath`: a prefix only counts
 * when the next character is a `/`, so `/care-homes` is not treated as a child
 * of `/care-home`.
 */
export function careHomeGroupForPath(pathname: string): Category | null {
  const owns = (base: string): boolean => pathname === base || pathname.startsWith(`${base}/`);
  return CARE_HOME_PATHS.some(owns) ? CARE_HOME : null;
}
